import React, { createContext, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';

import envs from 'envs';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

import { usePermissions, useUser } from 'hooks';

import { Mui } from 'components';
import { Dialogs } from 'components/common';
import { FormUser } from 'components/forms';

import BootGate from '../BootProvider/BootGate';

const PhoneConfirmationContext = createContext(false);

const useStyles = Mui.makeStyles(theme => ({
  paper: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

export function usePhoneConfirmationOpen() {
  return useContext(PhoneConfirmationContext);
}

function getUserPhoneNumber(user) {
  if (user?.phoneNumber) return String(user.phoneNumber);

  const numbers = user?.phoneNumbers || [];
  const primary = numbers.find(item => item.isPrimary) || numbers[0];
  if (!primary) return '';

  return `${primary.areaCode || ''}${primary.phoneNumber || ''}`;
}

function needsPhoneConfirmation(user, permissions) {
  if (!user?.uid && !user?.email) return false;

  const isSellerOrInstitution = Boolean(
    user?.isSeller || user?.isInstitution || permissions?.isSeller || permissions?.isInstitution,
  );
  if (!isSellerOrInstitution) return false;

  return user?.hasPhoneNumberConfirmed === false;
}

function PhoneConfirmationGuard({ children }) {
  const classes = useStyles();
  const { pathname } = useLocation();
  const { isAuthenticated, user, refreshing, patchUser } = useUser();
  const { permissions } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [dismissedKey, setDismissedKey] = useState(null);

  const userKey = user?.uid || user?.email || '';
  const mustConfirm =
    envs.ENVIRONMENT === 'production' &&
    needsPhoneConfirmation(user, permissions) &&
    pathname !== '/logout';
  const open = isAuthenticated && mustConfirm && dismissedKey !== userKey;

  const handleClose = () => setDismissedKey(userKey);

  const handleSubmit = profile => {
    if (profile) patchUser(profile);
    enqueueSnackbar('Telefone validado com sucesso!', { variant: 'success' });
    handleClose();
  };

  let content = children;
  if (isAuthenticated && refreshing && !(user?.uid || user?.email)) {
    content = <BootGate />;
  } else if (isAuthenticated) {
    content = (
      <>
        {children}
        <Dialogs.Confirmation
          open={open}
          onClose={handleClose}
          title="Confirme seu telefone"
          maxWidth="xs"
          PaperProps={{ className: classes.paper }}
          disableActions
          disableTypography>
          {open && (
            <FormUser.Phone
              intro="Cadastre e valide seu telefone por SMS."
              values={{ phoneNumber: getUserPhoneNumber(user) }}
              onSubmit={handleSubmit}
              onCancel={handleClose}
            />
          )}
        </Dialogs.Confirmation>
      </>
    );
  }

  return (
    <PhoneConfirmationContext.Provider value={open}>{content}</PhoneConfirmationContext.Provider>
  );
}

PhoneConfirmationGuard.propTypes = {
  children: PropTypes.node,
};

export default PhoneConfirmationGuard;
export * from './api';
